// Hash-based router. Every meaningful view gets its own URL, so a refresh keeps
// the user where they were, the browser's back/forward buttons stay consistent
// with what's on screen, and views are deep-linkable (brief: "impondérables
// UX"). Onboarding is the deliberate exception: it is one screen that swaps its
// own steps in place, so the recovery phrase it shows can never end up in the
// address bar, in history, or in a shared link.

export type Screen =
  | { name: 'welcome' }
  | { name: 'recover' }
  | { name: 'dashboard' }
  | { name: 'editProfile' }
  | { name: 'circle' }
  | { name: 'friendDetail'; circleId: string }
  | { name: 'invite'; circleId: string }
  | { name: 'shareId' }
  | { name: 'settings' }
  | { name: 'groups' };

function encode(s: Screen): string {
  switch (s.name) {
    case 'welcome':
      return '#/bienvenue';
    case 'recover':
      return '#/recuperer';
    case 'dashboard':
      return '#/profil';
    case 'editProfile':
      return '#/profil/modifier';
    case 'circle':
      return '#/cercle';
    case 'friendDetail':
      return '#/cercle/ami/' + encodeURIComponent(s.circleId);
    case 'invite':
      return '#/inviter/' + encodeURIComponent(s.circleId);
    case 'shareId':
      return '#/partager';
    case 'settings':
      return '#/reglages';
    case 'groups':
      return '#/groupes';
  }
}

function decode(rawHash: string): Screen | null {
  const path = rawHash.replace(/^#/, '') || '/';
  // `/circle/...` are the pre-rename paths, still accepted so links and
  // bookmarks shared before the app became Cercle keep working.
  // Stop at `?`/`&`/whitespace so junk a link-shim (e.g. Facebook's) appends
  // after the ID inside the hash — `fbclid=...` and the like — doesn't get
  // swallowed into the Circle ID and corrupt it.
  const friend = path.match(/^\/(?:cercle|circle)\/ami\/([^?&\s]+)/);
  if (friend) return { name: 'friendDetail', circleId: decodeURIComponent(friend[1]!) };
  const invite = path.match(/^\/inviter\/([^?&\s]+)/);
  if (invite) return { name: 'invite', circleId: decodeURIComponent(invite[1]!) };
  switch (path) {
    case '/bienvenue':
      return { name: 'welcome' };
    case '/recuperer':
      return { name: 'recover' };
    case '/profil':
      return { name: 'dashboard' };
    case '/profil/modifier':
      return { name: 'editProfile' };
    case '/cercle':
    case '/circle':
      return { name: 'circle' };
    case '/partager':
      return { name: 'shareId' };
    case '/reglages':
      return { name: 'settings' };
    case '/groupes':
      return { name: 'groups' };
    default:
      return null;
  }
}

let renderCb: (() => void) | null = null;

// A Circle ID captured from an invite link that arrived before the visitor had
// an identity. Held in sessionStorage so it survives onboarding (and a reload),
// then consumed once the friend is added.
const PENDING_INVITE_KEY = 'circle:pending-invite';

export function setPendingInvite(circleId: string): void {
  try {
    sessionStorage.setItem(PENDING_INVITE_KEY, circleId);
  } catch {
    /* sessionStorage unavailable — the invite just won't auto-resume */
  }
}

/** Read the pending invite without clearing it. */
export function pendingInvite(): string | null {
  try {
    return sessionStorage.getItem(PENDING_INVITE_KEY);
  } catch {
    return null;
  }
}

/** Read and clear the pending invite, if any. */
export function takePendingInvite(): string | null {
  try {
    const id = sessionStorage.getItem(PENDING_INVITE_KEY);
    if (id) sessionStorage.removeItem(PENDING_INVITE_KEY);
    return id;
  } catch {
    return null;
  }
}

/** Resolve the screen for the current URL, gated by whether an identity exists. */
export function currentScreen(hasIdentity: boolean): Screen {
  const decoded = decode(location.hash);
  if (!hasIdentity) {
    // `invite` is reachable without an identity: it's how someone lands from a
    // shared link and is guided through onboarding before adding the sender.
    if (decoded && (decoded.name === 'welcome' || decoded.name === 'recover' || decoded.name === 'invite')) {
      return decoded;
    }
    return { name: 'welcome' };
  }
  if (!decoded || decoded.name === 'welcome' || decoded.name === 'recover') return { name: 'dashboard' };
  return decoded;
}

/** Navigate to a screen. Updates the URL (so back/forward and refresh work). */
export function navigate(screen: Screen): void {
  const next = encode(screen);
  if (location.hash !== next) {
    location.hash = next; // fires 'hashchange' → render
  } else {
    renderCb?.(); // same route (e.g. refresh a list) — re-render explicitly
  }
}

/** Wire the render loop to URL changes and run the first paint. */
export function startRouter(render: () => void): void {
  renderCb = render;
  window.addEventListener('hashchange', render);
  render();
}
